/**
 * Adding or editing one thing from a list: a sheet from the trailing edge, sized to the form (`md`
 * 28rem, `lg` 32rem, `xl` 36rem, `2xl` 42rem), with its title, one line on what saving does, the
 * fields, and the actions in its foot (Cancel, then the one primary). The list stays where it was
 * behind it, so nothing on the page jumps when a form opens, and a phone gets the whole width.
 */

import { Sheet } from '@heroui/react';
import type { ReactNode } from 'react';

const WIDTH = { md: 'max-w-md', lg: 'max-w-lg', xl: 'max-w-xl', '2xl': 'max-w-2xl' } as const;

export function FormSheet({
  isOpen = true,
  title,
  description,
  width = 'lg',
  footer,
  onClose,
  children,
}: {
  isOpen?: boolean;
  title: string;
  description?: ReactNode;
  width?: keyof typeof WIDTH;
  /** The actions: Cancel and the one primary, at the trailing end. */
  footer?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <Sheet isOpen={isOpen} placement="right" onOpenChange={(o) => !o && onClose()}>
      <Sheet.Backdrop>
        <Sheet.Content className={`w-full ${WIDTH[width]}`}>
          <Sheet.Dialog aria-label={title}>
            <Sheet.Header className="flex-col items-start gap-1 pe-12">
              <Sheet.Heading>{title}</Sheet.Heading>
              {description ? <p className="text-xs text-muted">{description}</p> : null}
            </Sheet.Header>
            <Sheet.Body className="grid content-start gap-4 overflow-y-auto">{children}</Sheet.Body>
            {footer ? <Sheet.Footer className="flex flex-wrap items-center justify-end gap-2">{footer}</Sheet.Footer> : null}
            <Sheet.CloseTrigger />
          </Sheet.Dialog>
        </Sheet.Content>
      </Sheet.Backdrop>
    </Sheet>
  );
}

/** A labelled field in a form: the label above, the control, and a muted hint under it. */
export function FormField({ label, hint, children, className }: { label: ReactNode; hint?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <label className={`grid min-w-0 content-start gap-1.5 ${className ?? ''}`}>
      <span className="text-xs font-medium text-foreground">{label}</span>
      {children}
      {hint ? <span className="text-xs text-muted">{hint}</span> : null}
    </label>
  );
}
